import * as React from 'react';
import Axios, { AxiosPromise } from 'axios';

import ConfirmLogoComponent from './confirm-logo.component';
import { DtpRequest } from '../../logo-app/logo-upload/dtp-request.component';
import { ILogoVersionInfo, ILogoStateInfo } from '../logo.types';
import { IAppContext, withAppContext } from '../app-context';
import { localization, LocHelperInstance, langChangedName } from '../localization/localization';
import { DebuggerInstance } from '../debugger';
import { Markdown } from '../localization/markdown-wrapper';
import SimpleMessageDialog from './simple-message.dialog';

export interface IPreviewLogoComponentProps {
    onCompleted(success: boolean): void;
    handleConfirmationChanged(logoVersion: ILogoVersionInfo, value: boolean): void;
    handleLogoLoaded(logoVersion: ILogoVersionInfo, success: boolean): void;
    onDtpSendCompleted(result: boolean, email?: string, errorMsg?: string): void;
    logo: ILogoStateInfo;
    apiUrl: string;
    enableConfirm: boolean;
    enableCancel: boolean;
    appContext: IAppContext;
    showDtp: boolean;
}

export interface ILPreviewLogoComponentState {
    isLoading: boolean;
    loadingText?: string;
    enableVariants: boolean;
    dtpSend: boolean;
}

export interface ILogoStateUpdateRequest {
    normal: boolean;
    white: boolean;
    black: boolean;
}

/**
 * Handles previewing and accepting uploaded logo with variants original/white/black
 */
class PreviewLogoComponent extends React.PureComponent<IPreviewLogoComponentProps,ILPreviewLogoComponentState> {
    private langUpdateHandle = () => { this.forceUpdate(); }
    
    constructor(props: IPreviewLogoComponentProps) {
        super(props);

        this.state = {
            isLoading: false,
            enableVariants: true,
            dtpSend: false
        };
    }

    public componentDidMount() {
        LocHelperInstance.langChanged.addListener(langChangedName, this.langUpdateHandle);
    }

    public componentWillUnmount() {
        LocHelperInstance.langChanged.removeListener(langChangedName, this.langUpdateHandle);
    }

    private async onAccept() {
        this.setState({
            isLoading: true,
            loadingText: localization.commonLoc.saving
        });

        try {
            await this.sendConfirmation(this.props.logo);
            this.setState({
                isLoading: false,
                loadingText: undefined
            });
            this.props.onCompleted(true);
        } catch (error) {
            DebuggerInstance.log('onAccept-error:');
            DebuggerInstance.log(error);
            const dialog = (
                <SimpleMessageDialog
                    title={localization.uploadLoc.logoPreview.failedToAddLogo}
                    message={localization.uploadLoc.logoPreview.failedToAddLogo}
                    buttonText={localization.commonLoc.accept}
                    onClick={() => this.props.appContext.closeDialog()}
                />
            );

            this.setState({
                isLoading: false,
                loadingText: undefined
            });
            this.props.appContext.openDialog(dialog);
        }
    }

    private sendConfirmation(logosState: ILogoStateInfo): AxiosPromise<any> {
        const request: ILogoStateUpdateRequest = {
            normal: logosState.normal.accept,
            white: logosState.white.accept,
            black: logosState.black.accept
        }

        return Axios.put(this.props.apiUrl + this.props.logo.logoId + '/confirmation', request);
    }

    private onDtpCompleted(res: boolean, email?: string, errorMsg?: string): void {
        DebuggerInstance.log('onDtpStarted');
        this.setState({
            dtpSend: res
        });

        this.props.onDtpSendCompleted(res, email, errorMsg);
    }

    private onDtpStarted(): void {
        DebuggerInstance.log('onDtpStarted');
        this.setState({
            dtpSend: true
        });
    }

    private getLogoPreviews(logo: ILogoStateInfo): JSX.Element[] {
        const previews: JSX.Element[] = [];

        // Original colors
        let preview = (
            <div className="col-lg-4" key={'preview-original'}>
                <ConfirmLogoComponent
                    logoVersion={logo.normal}
                    useDarkBackground={false}
                    disableButtons={this.state.dtpSend}
                    showNotAcceptedOverlay={!logo.normal.accept}
                    notAcceptedText={localization.uploadLoc.logoConfirm.logoNotAcceptedOverlay}
                    onConfirmChanged={(id, value) => this.props.handleConfirmationChanged(id, value)}
                    onLoadComplete={(id, success) => this.props.handleLogoLoaded(id, success)}
                />
            </div>
        );
        previews.push(preview);

        // White colors
        preview = (
            <div className="col-lg-4" key={'preview-white'}>
                <ConfirmLogoComponent
                    logoVersion={logo.white}
                    useDarkBackground={true}
                    disableButtons={this.props.showDtp}
                    showNotAcceptedOverlay={!(logo.white.accept && logo.normal.accept)}
                    notAcceptedText={logo.normal.accept ? localization.uploadLoc.logoConfirm.variantNotAcceptedOverlay : localization.uploadLoc.logoConfirm.logoNotAcceptedOverlay}
                    onConfirmChanged={(id, value) => this.props.handleConfirmationChanged(id, value)}
                    onLoadComplete={(id, success) => this.props.handleLogoLoaded(id, success)}
                />
            </div>
        );
        previews.push(preview);

        // Black colors
        preview = (
            <div className="col-lg-4" key={'preview-black'}>
                <ConfirmLogoComponent
                    logoVersion={logo.black}
                    useDarkBackground={false}
                    disableButtons={this.props.showDtp}
                    showNotAcceptedOverlay={!(logo.black.accept && logo.normal.accept)}
                    notAcceptedText={logo.normal.accept ? localization.uploadLoc.logoConfirm.variantNotAcceptedOverlay : localization.uploadLoc.logoConfirm.logoNotAcceptedOverlay}
                    onConfirmChanged={(id, value) => this.props.handleConfirmationChanged(id, value)}
                    onLoadComplete={(id, success) => this.props.handleLogoLoaded(id, success)}
                />
            </div>
        );
        previews.push(preview);

        return previews;
    }

    public render() {
        const previews: JSX.Element[] = this.getLogoPreviews(this.props.logo);
        const dtpInfo = <Markdown text={localization.uploadLoc.logoPreview.dtpInfo} />;
        const info = <Markdown text={localization.uploadLoc.logoPreview.info} />

        let confirm: JSX.Element;
        if (this.props.showDtp) {
            confirm = (
                <div className="row pl-row-buttons">
                    <hr/>
                    <p>{dtpInfo}</p>
                    <DtpRequest logoId={this.props.logo.logoId}
                        apiUrl={this.props.apiUrl}
                        onSendStarted={() => this.onDtpStarted()}
                        onSendCompleted={(res, email, errorMsg) => this.onDtpCompleted(res, email, errorMsg)}
                        />
                </div>);
        } else {
            confirm = (
                <div className="row pl-row-buttons justify-content-center">
                    <button
                        onClick={() => this.onAccept()}
                        disabled={!this.props.enableConfirm}
                        className="main-button primary-style"
                    >
                        <Markdown text={localization.uploadLoc.logoPreview.confirmBtn} />
                    </button>
                </div>);
        }

        return (
            <div id="preview-logo-component"
                className="preview-logo-component-root"
                >
                <div className="container">
                    <div className="row pl-row-info">
                        <div className="col">
                            <p>{info}</p>
                        </div>
                    </div>
                    <div className="row pl-row-previews">{previews}</div>
                    {confirm}
                </div>
            </div>
        );
    }
    
}

export const PreviewLogo = withAppContext(PreviewLogoComponent);



// WEBPACK FOOTER //
// ./src/App/shared/components/preview-logo.component.tsx